import * as React from 'react';
import { ICheckboxGroupOption, ICheckboxGroupProps, CheckboxGroupOptionRenderType } from './CheckboxGroup.types';
import {
  BaseComponent,
  css,
  IRenderFunction,
  assign,
  autobind
} from 'office-ui-fabric-react/lib/Utilities';
import { FocusZone, FocusZoneDirection, IFocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ICheckboxProps, Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { getFileTypeIconProps } from '@uifabric/file-type-icons/lib/index';
import * as styles from './CheckboxGroup.scss';

/**
 * @internal
 */
export interface ICheckboxGroupState {
  keysChecked: string[];
}

/**
 * @internal
 */
export class CheckboxGroup extends BaseComponent<ICheckboxGroupProps, ICheckboxGroupState> {
  public static defaultProps: ICheckboxGroupProps = {
    options: []
  };

  protected root: HTMLElement | undefined;
  protected focusZone: IFocusZone | undefined;

  constructor(props: ICheckboxGroupProps) {
    super(props);
    this._warnMutuallyExclusive({
      checkedKeys: 'defaultCheckedKeys'
    });

    this.state = {
      keysChecked: ((props.checkedKeys !== undefined) ? props.checkedKeys : props.defaultCheckedKeys) || []
    };

    this.root = undefined;
    this.focusZone = undefined;
  }

  public componentWillReceiveProps(newProps: ICheckboxGroupProps): void {
    if (newProps.checkedKeys !== undefined) {
      this.setState({
        keysChecked: newProps.checkedKeys
      });
    }
  }

  public render(): JSX.Element {
    const { options, className } = this.props;
    const { keysChecked } = this.state;
    return (
      <div ref={ this._resolveRef('root') } role='group' className={ className } data-automationid='CheckboxGroup'>
        { options && options.length > 0 &&
          <FocusZone componentRef={ this._resolveRef('focusZone') } direction={ FocusZoneDirection.vertical } isCircularNavigation={ true }>
            { options.map((option: ICheckboxGroupOption, index: number) => {
              assign(option, {
                checked: keysChecked.indexOf(option.key) >= 0
              });
              return (
                <div
                  key={ option.key }
                  data-automationid='Checkbox'
                  data-is-checked={ option.checked }
                  data-checked-label={ option.label }
                >
                  <Checkbox
                    key={ option.key }
                    label={ option.label }
                    onRenderLabel={ getOnRenderLabelFunction(option) }
                    className={ css(styles.checkboxRoot,
                      { [`${styles.userCheckboxRoot}`]: option.renderType === CheckboxGroupOptionRenderType.user },
                      option.className) }
                    checked={ option.checked }
                    onChange={ this._onToggleCheckbox.bind(null, option) }
                  />
                </div>
              );
            }) }
          </FocusZone>
        }
      </div>
    );
  }

  @autobind
  private _onToggleCheckbox(option: ICheckboxGroupOption, ev: React.FormEvent<HTMLInputElement>, checked: boolean): void {
    const {
      checkedKeys, onChange
    } = this.props;

    // Only manage state in uncontrolled scenarios.
    if (checkedKeys === undefined) {
      this.setState((previousState: ICheckboxGroupState) => {
        const {
          keysChecked
        } = previousState;

        if (checked) {
          keysChecked.push(option.key);
        } else {
          let index = keysChecked.indexOf(option.key);
          if (index >= 0) {
            keysChecked.splice(index, 1);
          }
        }

        return {
          keysChecked: keysChecked
        };
      });
    }

    if (onChange) {
      onChange(ev, option, checked);
    }
  }
}

const renderUserTypeOptionLabel: IRenderFunction<ICheckboxGroupOption> = (props?: ICheckboxGroupOption) => {
  return (
    props ? (
      <Persona
        primaryText={ props.label }
        imageUrl={ props.imageSrc }
        size={ PersonaSize.size28 }
        className={ styles.checkboxLabelRoot }
      />
    ) : null
  );
};

const renderFileTypeOptionLabel: IRenderFunction<ICheckboxGroupOption> = (props?: ICheckboxGroupOption) => {
  return (
    props ? (
      <div className={ styles.checkboxLabelRoot } >
        <Icon
          { ...getFileTypeIconProps({
            extension: props.label,
            size: 16
          }) }
        />
        <span className={ styles.fileTypeCheckboxLabel }>{ props.label }</span>
      </div>
    ) : null
  );
};

function getOnRenderLabelFunction(option: ICheckboxGroupOption): IRenderFunction<ICheckboxProps> | undefined {
  let { onRenderLabel, renderType } = option;

  if (!onRenderLabel) {
    if (renderType === CheckboxGroupOptionRenderType.user) {
      onRenderLabel = renderUserTypeOptionLabel;
    } else if (renderType === CheckboxGroupOptionRenderType.fileType) {
      onRenderLabel = renderFileTypeOptionLabel;
    }
  }

  const finalOnRenderLabel = onRenderLabel;

  return (props?: ICheckboxProps, defaultRender?: IRenderFunction<ICheckboxProps>): JSX.Element | null => {
    return finalOnRenderLabel ?
      finalOnRenderLabel(option) :
      defaultRender && props ?
        defaultRender(props) :
        null;
  };
}
